import { client } from "@/hono";
import { useAuth } from "@/hooks/use-auth";
import { useAbsenceEditorRole } from "@/hooks/use-absence-editor-role";
import { useCitadel } from "@/hooks/use-citadel";
import { EditAbsenceModal } from "@/modals/users/edit-absence-modal";
import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { createStandardMenuItems, Menu } from "@msuite/ui/menu";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { type CellContext, RowActionsWrapper, useDataTableContext } from "@msuite/ui/table";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { getIsAbsenceEditable } from "@msuite/utils/absences";
import { guardString } from "@msuite/utils/strings";
import type { AbsenceListItem } from "@msuite/utils/types";
import React from "react";

export function RowActions({ cell }: CellContext<AbsenceListItem, "actions">) {
	/** Hooks */
	const { refetch } = useDataTableContext();
	const { t } = useTranslation();
	const asyncModal = useAsyncModal();
	const auth = useAuth();
	const editAbsenceModalDisclosure = useDisclosure();
	const { citadel } = useCitadel();
	const editorRole = useAbsenceEditorRole();

	/** Functions */
	async function handleOnDeleteAbsence() {
		toast.promise(
			async () => {
				const isConfirmed = await asyncModal.confirm();
				if (!isConfirmed) return;
				const response = await client.users[":userId"].absences[":absenceId"].$delete({
					param: {
						userId: guardString(auth.user?.id),
						absenceId: cell.row.original.id,
					},
				});
				await responseHandler(response);
				await refetch?.();
			},
			{
				loading: t("general.deleting"),
				success: t("general.successfully_deleted"),
			},
		);
	}

	/** Computed */
	// The delete route checks `delete Absence`; a member holds it for their own
	// requests, a role can widen or withdraw it. Without the rule the entry is
	// absent rather than offered and then refused.
	const canDeleteAbsence = citadel?.can("delete", "Absence") === true;

	/** Render */
	return (
		<React.Fragment>
			<RowActionsWrapper>
				<Menu
					items={createStandardMenuItems({
						onEdit: editAbsenceModalDisclosure.onOpen,
						onDelete: canDeleteAbsence ? handleOnDeleteAbsence : undefined,
						onDeleteOptions: {
							isDisabled: !getIsAbsenceEditable(cell.row.original, editorRole),
						},
						onEditOptions: {
							isDisabled: !getIsAbsenceEditable(cell.row.original, editorRole),
						},
					})}
				>
					<Button
						size="xxs"
						variant="ghost"
						icon={<Icon name="EllipsisVertical" />}
					/>
				</Menu>
			</RowActionsWrapper>
			{asyncModal.isOpen && (
				<ConfirmModal
					controller={asyncModal}
					message={t("absences.delete_absences_confirm_message")}
				/>
			)}
			{editAbsenceModalDisclosure.isOpen && (
				<EditAbsenceModal
					isOpen={editAbsenceModalDisclosure.isOpen}
					onClose={editAbsenceModalDisclosure.onClose}
					absenceId={cell.row.original.id}
				/>
			)}
		</React.Fragment>
	);
}
